import type { Swiper } from "swiper";
import type { Plugin } from "../core/plugin";
import { PluginRegistry } from "../core/pluginRegistry";

class TktHeroPagination implements Plugin {
  private swiper: Swiper | null = null;
  private syncDots: (() => void) | null = null;
  private swiperEl: HTMLElement | null = null;
  private onCarouselReady: (() => void) | null = null;
  private clickHandlers: Array<() => void> = [];

  constructor(private element: HTMLElement) {}

  init(): void {
    const swiperEl = this.element.querySelector<HTMLElement>(".swiper");
    const pagination = this.element.querySelector<HTMLElement>(
      ".tkt-heroB2B__pagination",
    );

    if (!swiperEl || !pagination) return;

    this.swiperEl = swiperEl;

    const dots = Array.from(
      pagination.querySelectorAll<HTMLElement>(".core-carouselPaginationDot"),
    );

    const syncDots = () => {
      if (!this.swiper) return;
      const activeIndex = this.swiper.activeIndex;

      dots.forEach((dot, i) => {
        const isActive = i === activeIndex;
        dot.classList.toggle("core-carouselPaginationDot--inactive", !isActive);
        dot.classList.toggle("core-carouselPaginationDot--rounded", !isActive);
        dot.setAttribute("aria-pressed", String(isActive));
        dot.setAttribute(
          "aria-label",
          isActive ? `Slide ${i + 1}, activo` : `Ir al slide ${i + 1}`,
        );
      });
    };
    this.syncDots = syncDots;

    dots.forEach((dot, i) => {
      const handler = () => this.swiper?.slideTo(i);
      this.clickHandlers.push(handler);
      dot.addEventListener("click", handler);
    });

    // El plugin "carousel" (Swiper) se inicializa en el mismo pase de
    // PluginRegistry pero *después* de este, porque el root del hero antecede
    // en el DOM al .swiper hijo. Nos suscribimos a "plugin-initialized" —
    // evento que el propio registry dispara sobre el elemento del carrusel
    // tras su init() — para conectar con la instancia real de Swiper y sus
    // eventos nativos en vez de vigilar mutaciones de clase a mano.
    const attachToSwiper = () => {
      const swiper = (swiperEl as HTMLElement & { swiper?: Swiper }).swiper;
      if (!swiper) return;

      this.swiper = swiper;
      swiper.on("slideChange", syncDots);
      syncDots();
    };

    if ((swiperEl as HTMLElement & { swiper?: Swiper }).swiper) {
      attachToSwiper();
    } else {
      this.onCarouselReady = attachToSwiper;
      swiperEl.addEventListener("plugin-initialized", this.onCarouselReady, {
        once: true,
      });
    }
  }

  destroy(): void {
    if (this.swiper && this.syncDots) {
      this.swiper.off("slideChange", this.syncDots);
    }
    if (this.swiperEl && this.onCarouselReady) {
      this.swiperEl.removeEventListener(
        "plugin-initialized",
        this.onCarouselReady,
      );
    }

    const pagination = this.element.querySelector<HTMLElement>(
      ".tkt-heroB2B__pagination",
    );
    if (pagination) {
      const dots = Array.from(
        pagination.querySelectorAll<HTMLElement>(".core-carouselPaginationDot"),
      );
      dots.forEach((dot, i) => {
        if (this.clickHandlers[i])
          dot.removeEventListener("click", this.clickHandlers[i]);
      });
    }
    this.clickHandlers = [];
  }
}

PluginRegistry.register("tktHeroPagination", TktHeroPagination);
